import { ProtectedRouter } from 'infrastructure/routing';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import ViewProjectLayout from './Show/ViewProjectLayout';
import {
  NEW_PROJECT_PATH,
  PROJECT_CYCLE_SEARCH,
  ACCESS_INFO_ACTIONS,
  ACCESS_INFO_SECTIONS,
  useProjectSecurityContext,
  BILLING_SCHEDULE_READ_LIST,
  BUDGET_READ_LIST,
  BUDGET_UPDATE,
  CREATE_CHANGE_REQUEST,
  CREATE_CRITICAL_PERIOD,
  CREATE_PROJECT,
  CREATE_REQUEST,
  CREATE_RISK,
  FINANCE_WASTE_GET_BY_ID,
  FINANCE_WASTE_UPDATE_WASTE,
  GET_CUSTOMER_REPORT,
  GRAPHQL_PROJECT_BUDGET_VIEW,
  GRAPHQL_PROJECT_BUSINESS_OUTCOME_VIEW,
  GRAPHQL_PROJECT_CUSTOMER_REPORT_VIEW,
  GRAPHQL_PROJECT_EVM_VIEW,
  GRAPHQL_PROJECT_MILESTONE_VIEW,
  GRAPHQL_PROJECT_RISK_VIEW,
  GRAPHQL_PROJECT_SDLC_VIEW,
  GRAPHQL_PROJECT_TEAM_VIEW,
  GRAPHQL_PROJECT_CLIENT_CONFIRMATION_VIEW,
  GRAPHQL_PROJECT_STAKEHOLDERS_VIEW,
  GRAPHQL_PROJECT_ENGINEERING_VIEW,
  PROJECT_CREATE_RISK,
  PROJECT_UPDATE_RISK,
  READ_CHANGE_REQUEST,
  READ_EVM,
  READ_LIST_DASHBOARD,
  REPORT_WASTE,
  SAVE_CUSTOMER_REPORT_AS_DRAFT,
  SAVE_REPORT_AS_DRAFT,
  SUBMIT_CUSTOMER_REPORT,
  SUBMIT_CUSTOMER_REPORT_GLOBAL,
  UPDATE_CHANGE_REQUEST,
  UPDATE_PROJECT,
  useNewProjectRoute,
  useNewProjectUrl,
  GRAPHQL_PROJECT_ASSUMPTION_REVIEW,
  SEARCH_RISK,
  GRAPHQL_PROJECT_CONTROL_PLANE_VIEW,
  GRAPHQL_CRITICAL_PERIOD_VIEW,
  START_PROJECT_REVIEW,
  GRAPHQL_PROJECT_ASSUMPTION_VIEW,
  DELIVERY_PROJECT_UPDATE_TEAM,
  GRAPHQL_PROJECT_CLIENT_PORTAL_VIEW,
  PROJECT_CREATE_CLIENT_TASK,
  PROJECT_READ_CLIENT_TASK,
  READ_CLIENT_TASK,
  CREATE_CLIENT_TASK,
} from 'domain/shared/newProject';
import CreateChangeRequestLayout from './finance/changeRequests/createupdate/Create/CreateChangeRequestLayout';
import UpdateChangeRequestLayout from './finance/changeRequests/createupdate/Update/UpdateChangeRequestLayout';
import PreviewChangeRequestLayout from './finance/changeRequests/preview/PreviewChangeRequestLayout';
import ReviewAssumptionLayout from './assumptions/Review/ReviewAssumptionLayout';
import { AssumptionReviewContext } from './assumptions/Review/AssumptionReviewContext';
import CreateProjectLayout from './createupdate/CreateProjectLayout';
import useFeatureToggle from 'framework/featureToggle';
import { Features } from 'infrastructure/featureToggle';
import CreateWasteLayout from './finance/waste/createupdate/CreateWasteLayout';
import UpdateWasteLayout from './finance/waste/createupdate/UpdateWasteLayout';
import PreviewWastePage from './finance/waste/preview/PreviewWastePage';
import ProjectsDashboard from './dashboard/List/ProjectsDashboard';
import UpdateBudgetLayout from './finance/budgets/update/UpdateBudgetLayout';
import CreateCustomerReportLayout from 'domain/projects/newProject/customerReport/createupdate/CreateCustomerReportLayout';
import ApproveCustomerReportLayout from 'domain/projects/newProject/customerReport/approve/ApproveCustomerReportLayout';
import UpdateCustomerReportLayout from 'domain/projects/newProject/customerReport/createupdate/UpdateCustomerReportLayout';
import OutcomesLayout from './businessOutcomes/OutcomesLayout';
import EVMLayout from './evm/EVMLayout';
import { ProtectedRoute } from 'framework/authorization';
import ProjectTeamLayout from './team/Show/ProjectTeamLayout';
import CreateRiskLayout from './risks/createupdate/CreateRiskLayout';
import SDLCLayout from './sdlc/SDLCLayout';
import FinanceLayout from './finance/FinanceLayout';
import CustomerReportContext from './customerReport/CustomerReportContext';
import UpdateProjectLayout from './createupdate/UpdateProjectLayout';
import NewProjectContext from './NewProjectContext';
import { ADD_BILLING_INFO } from './billing/constant';
import BillingInfoFormLayout from './billing/createupdate/BillingInfoFormLayout';
import { ProjectContext } from './context/ProjectContext';
import CreateCriticalPeriodLayout from './team/Show/ProjectCriticalPeriods/createupdate/CreateCriticalPeriodLayout';
import UpdateCriticalPeriodLayout from './team/Show/ProjectCriticalPeriods/createupdate/UpdateCriticalPeriodLayout';
import AcceptanceLayout from './acceptance/AcceptanceLayout';
import ProjectToolsLayout from './createupdate/ProjectToolsLayout';
import { ProjectTimelinePage } from './timeline';
import AssumptionsContext from './assumptions/AssumptionsContext';
import RisksLayout from './risks/RisksLayout';
import ViewRiskLayout from './risks/mainRiskSection/ViewRiskLayout';
import StakeholdersLayout from './stakeholders/StakeholdersLayout';
import ViewControlsLayout from './controls/ViewControlsLayout';
import { EngineeringLayout } from './engineering';
import { ReuseDataPage } from './reuseData';
import { CriticalPeriodContext } from './context/CriticalPeriodContext';
import ProjectReviewLayout from './projectReview/ProjectReviewLayout';
import SummaryReviewLayout from './projectReview/summaryReview/SummaryReviewLayout';
import ProjectClientPortalLayout from './clientPortal/ProjectClientPortalLayout';
import CreateClientPortalTaskLayout from './clientPortal/portalTasks/createUpdate/CreateClientPortalTaskLayout';
import ViewClientPortalTaskLayout from './clientPortal/portalTasks/mainTaskSection/ViewClientPortalTaskLayout';
import ConfirmAssignmentsLayout from './team/Show/AssignmentsConfirmation/ConfirmAssignmentsLayout';
import ProjectTeamPositionsLayout from './team/Show/ProjectTeamPositions/ProjectTeamPositionsLayout';
import InviteClientUsersDrawer from './clientPortal/portalClients/InviteClientUsersDrawer';
import ApproveClientUsersDrawer from './clientPortal/portalClients/ApproveClientUsersDrawer';
import { ClientPortalProjectContext } from './context/ClientPortalContext';
import { useProjectId } from 'domain/shared/project';

const RedirectToOutcomes = () => {
  const { id } = useParams();
  const { isAccessGranted } = useProjectSecurityContext();
  const { getOutcomesUrl, getControlsUrl } = useNewProjectUrl();
  return (
    <Navigate
      to={
        isAccessGranted(ACCESS_INFO_SECTIONS.CONTROL_PLANE, ACCESS_INFO_ACTIONS.VIEW)
          ? getControlsUrl(id)
          : getOutcomesUrl(id)
      }
    />
  );
};

const ProjectClientPortalRoute = () => {
  const projectId = useProjectId();

  return (
    <ClientPortalProjectContext id={projectId}>
      <ProjectClientPortalLayout />
    </ClientPortalProjectContext>
  );
};

const NewProjectRouter = () => {
  const { isFeatureEnabled } = useFeatureToggle();
  const {
    controlsTabRoute,
    dashboardRoute,
    listRoute,
    singleEntityRoute,
    outcomesTabRoute,
    financeTabRoute,
    risksTabRoute,
    evmTabRoute,
    teamTabRoute,
    timelineTabRoute,
    sdlcTabRoute,
    assumptionsTabRoute,
    reviewAssumptionFullRoute,
    acceptanceTabRoute,
    stakeholdersTabRoute,
    reuseDataFullRoute,
    createCriticalPeriodRoute,
    updateCriticalPeriodRoute,
    customerReportTabRoute,
    createRiskFullRoute,
    createClientPortalTaskRoute,
    viewClientPortalTaskRoute,
    createProjectReviewFullRoute,
    summaryReviewFullRoute,
    updateRiskFullRoute,
    confirmVirtualFullRoute,
    teamPositionsFullRoute,
    updateBudgetFullRoute,
    newChangeRequestFullRoute,
    updateChangeRequestFullRoute,
    previewChangeRequestFullRoute,
    newCreateWasteFullRoute,
    updateWasteFullRoute,
    previewWasteFullRoute,
    createCustomerReportFullRoute,
    updateCustomerReportFullRoute,
    approveCustomerReportFullRoute,
    billingRoute,
    engineeringRoute,
    clientPortalTabRoute,
    clientPortalInviteTabRoute,
    clientPortalApproveInviteTabRoute,
  } = useNewProjectRoute();

  return (
    <ProtectedRouter basePath={NEW_PROJECT_PATH}>
      <Routes>
        {isFeatureEnabled(Features.NEW_PROJECT_DASHBOARD) && (
          <Route
            path={dashboardRoute}
            element={
              <ProtectedRoute permissionToCheck={READ_LIST_DASHBOARD}>
                <ProjectsDashboard />
              </ProtectedRoute>
            }
          />
        )}

        <Route
          path={singleEntityRoute}
          element={
            <ProjectContext>
              <ViewProjectLayout />
            </ProjectContext>
          }
        >
          {isFeatureEnabled(Features.CONTROLS) && (
            <Route
              path={controlsTabRoute}
              element={
                <ProtectedRoute permissionToCheck={[GRAPHQL_PROJECT_CONTROL_PLANE_VIEW]}>
                  <ViewControlsLayout />
                </ProtectedRoute>
              }
            />
          )}
          <Route
            path={outcomesTabRoute}
            element={
              <ProtectedRoute
                permissionToCheck={[
                  GRAPHQL_PROJECT_BUSINESS_OUTCOME_VIEW,
                  GRAPHQL_PROJECT_MILESTONE_VIEW,
                  GRAPHQL_PROJECT_RISK_VIEW,
                ]}
              >
                <OutcomesLayout />
              </ProtectedRoute>
            }
          />
          <Route
            path={financeTabRoute}
            element={
              // DELIVERY_BUDGET_READ_LIST from global
              <ProtectedRoute
                permissionToCheck={[BUDGET_READ_LIST, BILLING_SCHEDULE_READ_LIST, GRAPHQL_PROJECT_BUDGET_VIEW]}
              >
                <FinanceLayout />
              </ProtectedRoute>
            }
          />
          <Route
            path={risksTabRoute}
            element={
              <ProtectedRoute permissionToCheck={GRAPHQL_PROJECT_RISK_VIEW}>
                <RisksLayout />
              </ProtectedRoute>
            }
          />
          {isFeatureEnabled(Features.NEW_PROJECT_EVM) && (
            <Route
              path={evmTabRoute}
              element={
                <ProtectedRoute permissionToCheck={[READ_EVM, GRAPHQL_PROJECT_EVM_VIEW]}>
                  <EVMLayout />
                </ProtectedRoute>
              }
            />
          )}

          <Route
            path={customerReportTabRoute}
            element={
              <ProtectedRoute permissionToCheck={[GRAPHQL_PROJECT_CUSTOMER_REPORT_VIEW]}>
                <CustomerReportContext />
              </ProtectedRoute>
            }
          />
          {isFeatureEnabled(Features.NEW_PROJECT_SDLC) && (
            <Route
              path={sdlcTabRoute}
              element={
                <ProtectedRoute permissionToCheck={[PROJECT_CYCLE_SEARCH, GRAPHQL_PROJECT_SDLC_VIEW]}>
                  <SDLCLayout />
                </ProtectedRoute>
              }
            />
          )}
          {isFeatureEnabled(Features.PROJECT_TEAM_TAB) && (
            <Route
              path={teamTabRoute}
              element={
                <ProtectedRoute permissionToCheck={GRAPHQL_PROJECT_TEAM_VIEW}>
                  <ProjectTeamLayout />
                </ProtectedRoute>
              }
            />
          )}
          {isFeatureEnabled(Features.NEW_PROJECT_TIMELINE) && (
            <Route
              path={timelineTabRoute}
              element={
                <ProtectedRoute permissionToCheck={GRAPHQL_PROJECT_TEAM_VIEW}>
                  <ProjectTimelinePage />
                </ProtectedRoute>
              }
            />
          )}
          <Route
            path={assumptionsTabRoute}
            element={
              <ProtectedRoute permissionToCheck={GRAPHQL_PROJECT_ASSUMPTION_VIEW}>
                <AssumptionsContext />
              </ProtectedRoute>
            }
          />
          <Route
            path={acceptanceTabRoute}
            element={
              <ProtectedRoute permissionToCheck={GRAPHQL_PROJECT_CLIENT_CONFIRMATION_VIEW}>
                <AcceptanceLayout />
              </ProtectedRoute>
            }
          />
          {isFeatureEnabled(Features.NEW_PROJECT_STAKEHOLDERS) && (
            <Route
              path={stakeholdersTabRoute}
              element={
                <ProtectedRoute permissionToCheck={GRAPHQL_PROJECT_STAKEHOLDERS_VIEW}>
                  <StakeholdersLayout />
                </ProtectedRoute>
              }
            />
          )}
          {isFeatureEnabled(Features.CLIENT_PORTAL) && (
            <Route
              path={clientPortalTabRoute}
              element={
                <ProtectedRoute permissionToCheck={GRAPHQL_PROJECT_CLIENT_PORTAL_VIEW}>
                  <ProjectClientPortalRoute />
                </ProtectedRoute>
              }
            >
              <Route index element={null} />
              <Route path={clientPortalInviteTabRoute} element={<InviteClientUsersDrawer />} />
              <Route path={clientPortalApproveInviteTabRoute} element={<ApproveClientUsersDrawer />} />
            </Route>
          )}
          <Route index element={<RedirectToOutcomes />} />
          {isFeatureEnabled(Features.NEW_PROJECT_ENGINEERING) && (
            <Route
              path={engineeringRoute}
              element={
                <ProtectedRoute permissionToCheck={GRAPHQL_PROJECT_ENGINEERING_VIEW}>
                  <EngineeringLayout />
                </ProtectedRoute>
              }
            />
          )}
        </Route>

        {isFeatureEnabled(Features.NEW_PROJECT_REVIEW) && (
          <Route
            path={createProjectReviewFullRoute}
            element={
              <ProtectedRoute permissionToCheck={START_PROJECT_REVIEW}>
                <ProjectContext>
                  <ProjectReviewLayout />
                </ProjectContext>
              </ProtectedRoute>
            }
          />
        )}
        {isFeatureEnabled(Features.NEW_PROJECT_REVIEW) && (
          <Route
            path={summaryReviewFullRoute}
            element={
              <ProtectedRoute permissionToCheck={START_PROJECT_REVIEW}>
                <ProjectContext>
                  <SummaryReviewLayout />
                </ProjectContext>
              </ProtectedRoute>
            }
          />
        )}
        <Route
          path={createCriticalPeriodRoute}
          element={
            <NewProjectContext permissionToCheck={CREATE_CRITICAL_PERIOD}>
              <CreateCriticalPeriodLayout />
            </NewProjectContext>
          }
        />
        <Route
          path={updateCriticalPeriodRoute}
          element={
            <CriticalPeriodContext permissionToCheck={GRAPHQL_CRITICAL_PERIOD_VIEW}>
              <UpdateCriticalPeriodLayout />
            </CriticalPeriodContext>
          }
        />
        <Route
          path={createRiskFullRoute}
          element={
            <NewProjectContext permissionToCheck={[CREATE_RISK, PROJECT_CREATE_RISK]}>
              <CreateRiskLayout />
            </NewProjectContext>
          }
        />
        {isFeatureEnabled(Features.CLIENT_PORTAL) && (
          <Route
            path={createClientPortalTaskRoute}
            element={
              <NewProjectContext permissionToCheck={[PROJECT_CREATE_CLIENT_TASK, CREATE_CLIENT_TASK]}>
                <CreateClientPortalTaskLayout />
              </NewProjectContext>
            }
          />
        )}
        {isFeatureEnabled(Features.CLIENT_PORTAL) && (
          <Route
            path={viewClientPortalTaskRoute}
            element={
              <NewProjectContext permissionToCheck={[PROJECT_READ_CLIENT_TASK, READ_CLIENT_TASK]}>
                <ViewClientPortalTaskLayout />
              </NewProjectContext>
            }
          />
        )}
        <Route
          path={updateRiskFullRoute}
          element={
            <NewProjectContext
              permissionToCheck={[SEARCH_RISK, PROJECT_UPDATE_RISK]}
              subEntityType={'Risk'}
              getSubEntityId={params => params.riskId}
            >
              <ViewRiskLayout />
            </NewProjectContext>
          }
        />
        {isFeatureEnabled(Features.STAFFING_ASSIGNMENTS) && (
          <Route
            path={confirmVirtualFullRoute}
            element={
              <ProjectContext>
                <ConfirmAssignmentsLayout />
              </ProjectContext>
            }
          />
        )}
        {isFeatureEnabled(Features.STAFFING_ASSIGNMENTS) && (
          <Route
            path={teamPositionsFullRoute}
            element={
              <ProtectedRoute permissionToCheck={DELIVERY_PROJECT_UPDATE_TEAM}>
                <ProjectContext>
                  <ProjectTeamPositionsLayout />
                </ProjectContext>
              </ProtectedRoute>
            }
          />
        )}
        <Route
          path={updateBudgetFullRoute}
          element={
            <ProtectedRoute permissionToCheck={BUDGET_UPDATE}>
              <UpdateBudgetLayout />
            </ProtectedRoute>
          }
        />

        <Route
          path={newChangeRequestFullRoute}
          element={
            <NewProjectContext permissionToCheck={[CREATE_CHANGE_REQUEST, CREATE_REQUEST]}>
              <CreateChangeRequestLayout />
            </NewProjectContext>
          }
        />
        <Route
          path={updateChangeRequestFullRoute}
          element={
            <NewProjectContext
              permissionToCheck={[UPDATE_CHANGE_REQUEST]}
              subEntityType={'ChangeRequest'}
              getSubEntityId={params => params.requestId}
            >
              <UpdateChangeRequestLayout />
            </NewProjectContext>
          }
        />
        <Route
          path={previewChangeRequestFullRoute}
          element={
            <NewProjectContext
              permissionToCheck={[READ_CHANGE_REQUEST]}
              subEntityType={'ChangeRequest'}
              getSubEntityId={params => params.requestId}
            >
              <PreviewChangeRequestLayout />
            </NewProjectContext>
          }
        />
        <Route
          path={reviewAssumptionFullRoute}
          element={
            <AssumptionReviewContext permissionToCheck={GRAPHQL_PROJECT_ASSUMPTION_REVIEW}>
              <ReviewAssumptionLayout />
            </AssumptionReviewContext>
          }
        />
        <Route
          path={newCreateWasteFullRoute}
          element={
            <ProtectedRoute permissionToCheck={[REPORT_WASTE]}>
              <CreateWasteLayout />
            </ProtectedRoute>
          }
        />
        <Route
          path={updateWasteFullRoute}
          element={
            <ProtectedRoute permissionToCheck={[FINANCE_WASTE_UPDATE_WASTE]}>
              <UpdateWasteLayout />
            </ProtectedRoute>
          }
        />
        <Route
          path={previewWasteFullRoute}
          element={
            <ProtectedRoute permissionToCheck={[FINANCE_WASTE_GET_BY_ID]}>
              <PreviewWastePage />
            </ProtectedRoute>
          }
        />
        {isFeatureEnabled(Features.CREATE_PROJECT) && (
          <Route
            path={`${listRoute}/create`}
            element={
              <NewProjectContext permissionToCheck={CREATE_PROJECT}>
                <CreateProjectLayout />
              </NewProjectContext>
            }
          />
        )}
        {isFeatureEnabled(Features.UPDATE_PROJECT) && (
          <Route
            path={`${singleEntityRoute}/update`}
            element={
              <NewProjectContext permissionToCheck={UPDATE_PROJECT}>
                <UpdateProjectLayout />
              </NewProjectContext>
            }
          />
        )}
        {isFeatureEnabled(Features.CREATE_PROJECT) && (
          <Route
            path={`${singleEntityRoute}/tools`}
            element={
              <NewProjectContext permissionToCheck={CREATE_PROJECT}>
                <ProjectToolsLayout />
              </NewProjectContext>
            }
          />
        )}

        <Route
          path={createCustomerReportFullRoute}
          element={
            <NewProjectContext permissionToCheck={[SUBMIT_CUSTOMER_REPORT_GLOBAL, SUBMIT_CUSTOMER_REPORT]}>
              <CreateCustomerReportLayout />
            </NewProjectContext>
          }
        />
        <Route
          path={updateCustomerReportFullRoute}
          element={
            <NewProjectContext
              permissionToCheck={[SAVE_CUSTOMER_REPORT_AS_DRAFT, SAVE_REPORT_AS_DRAFT]}
              subEntityType={'CustomerReport'}
              getSubEntityId={params => params.reportId}
            >
              <UpdateCustomerReportLayout />
            </NewProjectContext>
          }
        />
        <Route
          path={approveCustomerReportFullRoute}
          element={
            <NewProjectContext
              permissionToCheck={GET_CUSTOMER_REPORT}
              subEntityType={'CustomerReport'}
              getSubEntityId={params => params.reportId}
            >
              <ApproveCustomerReportLayout />
            </NewProjectContext>
          }
        />

        <Route
          path={billingRoute}
          element={
            <NewProjectContext permissionToCheck={[ADD_BILLING_INFO, BILLING_SCHEDULE_READ_LIST]}>
              <BillingInfoFormLayout />
            </NewProjectContext>
          }
        />
        {isFeatureEnabled(Features.NEW_PROJECT_REUSE_DATA) && (
          <Route
            path={reuseDataFullRoute}
            element={
              <ProjectContext>
                <ReuseDataPage />
              </ProjectContext>
            }
          />
        )}
      </Routes>
    </ProtectedRouter>
  );
};

NewProjectRouter.getRouterPath = () => `${NEW_PROJECT_PATH}/*`;

export default NewProjectRouter;
